import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import swal from "sweetalert";
import { useQuery, useDetail, useSet } from "seed/gql";
import { SET_FLOW } from "seed/gql/queries";
import { usePost } from "seed/api";
import { Loading } from "seed/helpers";

import l from "components/util/l";
import * as parser from "components/studio/Controller.lib.parser";
import View from "components/studio/Studio.view";

const Studio = forwardRef(({ match, isAnonymous = false, isValidator = false, onPostSave = () => { } }, ref) => {

  const userId = sessionStorage.getItem("id");
  const flowId = match?.params?.flowId;
  const isDeveloper = sessionStorage.getItem("is_developer") == "true";
  const [activePageId, setActivePageId] = useState(sessionStorage.getItem("page_" + flowId));
  const [flowHistory, setFlowHistory] = useState([{ nodes: [], edges: [], annotations: [] }]);
  const [flowHistoryPosition, setFlowHistoryPosition] = useState(0);
  const [callActivateRequiredApps, reqActivateRequiredApps] = usePost('/flows/activate_required_apps');
  const execModeRef = useRef(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (window.innerWidth < 540 && !sessionStorage.getItem("mobileWarningShown"))
      swal("Módulo parcialmente habilitado para moviles",
        l("Para una mejor experiencia, te recomendamos acceder a este sitio desde un ordenador de escritorio o portátil.%SA003"),
        "warning").then(() => {
          sessionStorage.setItem("mobileWarningShown", true)
        });
    callActivateRequiredApps({
      flowId: flowId,
    })
  }, []);

  const filterPage = `flow.id=${flowId} AND ` + (activePageId ? `page.id=${activePageId}` : "page.isDefault=True");

  const reqUser = useDetail(`{
    user {
      firstName
        lastName
        email
        isAnonymous
        license {
          name
      }
      accountSettings
    }
  }`, userId)

  const [callSetFlow] = useSet(SET_FLOW);
  const reqFlow = useDetail(`{
    flow {
      name
      description
      token
      settings
      presentationSettings
      participants {
        role {
          name
        }
        user {}
      }
      user {
        email
        accountSettings
      }
      theme {
        primaryColor
        accentColor
      }
    }
  }`, flowId, {
    onCompleted: (data) => {

      if(!isValidator)
        document.title = `${data.flow.name} - Workix Studio`

      if (!data.flow.settings?.default_debug_execution)
        callSetFlow({
          id: data.flow.id,
          settings: {
            ...data.flow.settings ?? {},
            default_debug_execution: true,
          }
        });

      if ((data.flow.presentationSettings?.selected_lang ?? "auto") == "auto") {
          callSetFlow({
            id: data.flow.id,
            presentationSettings: {
              ...data.flow.presentationSettings,
              default_lang: (navigator.language || navigator.userLanguage || "en").split(/[-_.]/)[0]
            }
          });
        }
      
    }
  });

  const reqPages = useQuery(`{
      flowPages {
        name
        isDefault
      }
    }`, `flow.id = ${flowId}`, {
    onCompleted: (data) => {
      let defaultPageId = 0
      for (let page of data.flowPages)
        if ((page.isDefault && defaultPageId == 0) || (page.id == sessionStorage.getItem("page_" + flowId)))
          defaultPageId = page.id
      if (activePageId != defaultPageId) {
        setActivePageId(defaultPageId)
        sessionStorage.setItem("page_" + flowId, defaultPageId)
      }
    },
    orderBy: "name",
    notifyOnNetworkStatusChange: false
  })

  const reqNodes = useQuery(`{
    flowNodes {
      createdAt
      inputValues
      outputValues
      uiSettings
      isEnabled
      action { 
        name
        type
        isEditable
        inputStructure
        outputStructure
        uiSettings
        isAnnotation
        app { 
          name
          uiSettings
        }
      }
      isVerified
      rootName
      rootDescription
      rootSettings
      flow { name }
    }
  }`, filterPage, {
    onCompleted: (data) => {
      const flowNodes = data?.flowNodes ?? [];
      const nodes = parser.parseNodesGraphqlToUi(flowNodes);
      setFlowHistory((prevHistory) => {
        if (typeof prevHistory == "object" && prevHistory.length > 0) {
          prevHistory[0].nodes = nodes;
        }
        return prevHistory;
      });
    },
     notifyOnNetworkStatusChange: false
  });

  const reqEdges = useQuery(`{
    flowEdges {
      type
      createdAt
      sourceName
      targetName
      source { }
      target { }
      flow { name }
    }
  }`, filterPage, {
    onCompleted: (data) => {
      const flowEdges = data?.flowEdges ?? [];
      const edges = parser.parseEdgesGraphqlToUi(flowEdges);
      setFlowHistory((prevHistory) => {
        if (typeof prevHistory == "object" && prevHistory.length > 0) {
          prevHistory[0].edges = edges;
        }
        return prevHistory;
      });
    },
    notifyOnNetworkStatusChange: false
  });

  const reqAnnotations = useQuery(`{
    flowAnnotations {
      createdAt
      inputValues
      uiSettings      
      action { 
        name
        type
        isEditable
        inputStructure
        outputStructure
        uiSettings
        isAnnotation
        app { 
          name
          uiSettings
        }
      }
      flow { name }
    }
  }`, filterPage, {
    onCompleted: (data) => {
      const flowAnnotations = data?.flowAnnotations ?? [];
      const annotations = parser.parseAnnotationsGraphqlToUi(flowAnnotations);
      setFlowHistory((prevHistory) => {
        if (typeof prevHistory == "object" && prevHistory.length > 0) {
          prevHistory[0].annotations = annotations;
        }
        return prevHistory;
      });
    },
    notifyOnNetworkStatusChange: false
  });

  const reqActions = useQuery(`{
    actions {
      name
      type
      isEditable
      inputStructure
      outputStructure
      uiSettings
      isAnnotation
      app { name }
    }
  }`, ``, {
    notifyOnNetworkStatusChange: false
  })

  const reloadFlow = () => {
    setFlowHistory([{ nodes: [], edges: [], annotations: [] }]);
    setFlowHistoryPosition(0);
    reqFlow.refetch();
    reqNodes.refetch();
    reqEdges.refetch();
    reqAnnotations.refetch();
  }

  const updateHistory = (allNodes, edges) => {
    const nodes = allNodes.filter((item) => !item.isAnnotation);
    const annotations = allNodes.filter((item) => item.isAnnotation);
    setFlowHistory((prev) => [...prev.slice(0, flowHistoryPosition + 1), { nodes: nodes, edges: edges, annotations: annotations }]);
    setFlowHistoryPosition((prev) => prev + 1);
  }

  const onClickCloseModalController = () => {
    if (controllerRef.current) {
      controllerRef.current.onClickCloseModalRoute();
    }
  }

  useEffect(() => {
    reloadFlow();
  }, [activePageId]);

  useImperativeHandle(ref, () => ({
    reloadFlow
  }), []);

  if (reqFlow.loading || reqNodes.loading || reqEdges.loading || reqPages.loading ||
    reqAnnotations.loading || reqActions.loading || reqUser.loading)
    return <PlaceholderView />;
  if (reqFlow.error || reqNodes.error || reqEdges.error || reqPages.error ||
    reqAnnotations.error || reqActions.error || reqUser.error)
    return <div></div>;

  const { flow = {} } = reqFlow.data;
  const { flowNodes = [] } = reqNodes.data;
  const { flowEdges = [] } = reqEdges.data;
  const { flowAnnotations = [] } = reqAnnotations.data;
  const { actions = [] } = reqActions.data;
  const { user = {} } = reqUser.data;
  const { flowPages = [] } = reqPages.data;


  const isOwner = flow?.user.id == userId
  const isEditor =
    (flow.participants ?? [])
      .filter(participant => participant.type == "EDITOR")
      .map(participant => participant.user?.id).includes(parseInt(userId)) || isOwner
  if (!isEditor) return window.location.replace("/app")


  const nodes = parser.parseNodesGraphqlToUi(flowNodes);
  const edges = parser.parseEdgesGraphqlToUi(flowEdges);
  const annotations = parser.parseAnnotationsGraphqlToUi(flowAnnotations);

  return (
    <View
      flow={flow}
      pages={flowPages}
      user={user}
      nodes={nodes}
      edges={edges}
      annotations={annotations}
      actions={actions}
      activePageId={activePageId}
      flowHistory={flowHistory}
      flowHistoryPosition={flowHistoryPosition}
      isAnonymous={isAnonymous}
      isOwner={isOwner}
      isValidator={isValidator}
      isEditor={isEditor}
      isDeveloper={isDeveloper}
      execModeRef={execModeRef}
      controllerRef={controllerRef}
      setActivePageId={setActivePageId}
      setFlowHistoryPosition={setFlowHistoryPosition}
      reloadFlow={reloadFlow}
      onPostSave={onPostSave}
      updateHistory={updateHistory}
      onClickCloseModalController={onClickCloseModalController}
    />
  );
});

Studio.displayName = "Studio";

const PlaceholderView = () =>
  <div className="content container-fluid p-0">
    <div className="d-flex justify-content-center">
      <div className="bg-white" style={{ width: "100%", height: "100vh", overflowY: "auto" }}>
        <div class="card-body" style={{ padding: "10rem 0" }}>
          <Loading />
        </div>
      </div>
    </div>
  </div>

export default Studio;